import { useContext, type PointerEventHandler } from 'react'
import { type BranchOption, type ResolvedBranchNode } from '@/lib/branchTypes'
import { ChoosePathDesignContext, PRIMARY_PLAN_CARDS } from '../choosePathDesigns'
import { splitPlanLabel } from '../splitPlanLabel'
import { PlanAPreviewChevron } from './PlanAPreviewChevron'

type PrimaryButton2ChoicesFiveProps = {
  option: BranchOption
  targetNode: ResolvedBranchNode | null
  inverted: boolean
  showHoverStyleByDefault: boolean
  // In the expanded plan layout the recommended plan grows a preview on hover;
  // render the down-chevron beneath the button as the cue + extra hover target.
  showPreviewChevron: boolean
  onClick: () => void
  onPointerEnter?: PointerEventHandler<HTMLElement>
  onPointerLeave?: PointerEventHandler<HTMLElement>
}

// "Our Recommendation" CTA for 5-up plan pickers; sits in its own file so we can size it differently from the two-choice variant.
export const PrimaryButton2ChoicesFive = ({
  option,
  targetNode,
  inverted,
  showHoverStyleByDefault,
  showPreviewChevron,
  onClick,
  onPointerEnter,
  onPointerLeave,
}: PrimaryButton2ChoicesFiveProps) => {
  const { prefix, suffix } = splitPlanLabel(option.label)
  const design = useContext(ChoosePathDesignContext)
  const cardSpec = PRIMARY_PLAN_CARDS[design]
  // `group` here makes the button + chevron a single hover target: hovering
  // either one drives the button's background tint and the chevron's darken +
  // grow together (see the `group-hover:` styles in PlanAPreviewChevron).
  if (cardSpec?.layout === 'big-letter') {
    return (
      <div className="group flex-1">
        <button
          onClick={onClick}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          className={`${cardSpec.buttonClassName} ${inverted ? 'bg-black text-white border-black' : ''}`}
        >
          {prefix && <span className={cardSpec.prefixClassName}>{prefix}</span>}
          <span className={cardSpec.letterClassName}>{suffix}</span>
          {targetNode?.longSubtitle && (
            <div className={`${cardSpec.subtitleClassName} ${inverted ? 'text-white/80' : ''}`}>
              {targetNode.longSubtitle}
            </div>
          )}
        </button>
        {showPreviewChevron && (
          <PlanAPreviewChevron
            onClick={onClick}
            onPointerEnter={onPointerEnter}
            onPointerLeave={onPointerLeave}
          />
        )}
      </div>
    )
  }
  return (
    <div className="group flex-1">
      <button
        onClick={onClick}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        className={
          // A 'stock'-layout card spec swaps the chrome classes only (the red
          // recommended-plan border); the selected/inverted state keeps the
          // stock black fill on every design.
          cardSpec && !inverted
            ? cardSpec.buttonClassName
            : `rounded-md font-bold tracking-wide transition-colors cursor-pointer border flex flex-col flex-1 items-center w-full justify-center py-4 px-3 sm:px-3 md:px-4 border-4 text-2xl text-nowrap min-h-[110px] ${inverted ? 'bg-black text-white border-black' : `border-current ${showHoverStyleByDefault ? 'group-hover:bg-black/5' : 'bg-transparent group-hover:bg-black/5'}`}`
        }
      >
        {prefix ? (
          <span className="leading-none">
            <span className="block md:inline text-[10px] sm:text-xs md:text-3xl font-normal md:font-bold tracking-wider md:tracking-wide">{prefix}</span>
            <span className="block md:inline md:ml-2 text-3xl sm:text-4xl md:text-3xl leading-none">{suffix}</span>
          </span>
        ) : (
          option.label
        )}
        {targetNode?.longSubtitle && <div className={`font-normal tracking-wide text-balance hidden font-serif md:block min-w-[140px] text-wrap  text-center mt-2 text-sm ${inverted ? 'text-white/80' : 'text-black/90'}`}>{targetNode.longSubtitle}</div>}
      </button>
      {showPreviewChevron && (
        <PlanAPreviewChevron
          onClick={onClick}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
        />
      )}
            {/* <div className="font-mono text-center font-normal mt-3 text-[9px] text-gray-500 text-nowrap">(Our Recommendation)</div> */}

    </div>
  )
}
